import { shortCurrencyFormatter } from 'components/KPIs/helpers';
import { currencyFormatter } from 'components/KPIs/helpers';
import { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllCompaniesMarginsPerDate } from 'redux/analytics/customerSlice';
import SearchableMultiSelect from 'components/UI/Atoms/inputs/selects/searchableMultiselect';
import { generateColorSet } from 'utils/colors';

// Safely convert YYYY-MM-DD strings into local timestamps to avoid timezone shifts
const toLocalTimestamp = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            const [year, month, day] = trimmed.split('-').map(Number);
            return new Date(year, month - 1, day).getTime();
        }
    }
    const parsed = new Date(value);
    const time = parsed.getTime();
    return Number.isNaN(time) ? null : time;
};


/**
 * Props:
    * - startDate: string (YYYY-MM-DD)
    * - endDate: string (YYYY-MM-DD)
    * - searchTerm: string (optional)
    * - ordering: string (optional)
    * - height: number (optional, default 420)
    * - onGranularityChange: function (optional, called when user changes granularity)
 */
function AllCustomersRevenueProfitChart({
    startDate,
    endDate,
    searchTerm = '',
    ordering = 'revenue',
    height = 420,
}) {

    // ---------- Controls ----------
    const [granularity, setGranularity] = useState('month'); // 'day' | 'month'

    // which metrics to show
    const [showRevenue, setShowRevenue] = useState(true);
    const [showProfit, setShowProfit] = useState(true);
    const [showLoads, setShowLoads] = useState(true);

    // ---------- Fetch data ----------
    const dispatch = useDispatch();
    
    const { error: allCompaniesMarginPerdateError, loading: allCompaniesMarginPerdateLoading, data: allCompaniesMarginsPerDate } = useSelector(
        (state) => state.customers.allCompaniesMarginsPerDate
    );
    useEffect(() => {
        dispatch(
            fetchAllCompaniesMarginsPerDate({
                startDate,
                endDate,
                searchTerm: searchTerm,
                ordering,
                granularity,
            })
        );
    }, [dispatch, startDate, endDate, searchTerm, ordering, granularity]);

    // list of customers
    const allCustomers = (allCompaniesMarginsPerDate?.results || []).map((c) => c.customer_name);
    const [selectedCustomers, setSelectedCustomers] = useState(new Set());
    const [customColorMap, setCustomColorMap] = useState({});

    const uniqueColors = useMemo(() => {
        const map = { ...customColorMap };
        const names = Array.from(selectedCustomers);
        if (names.length > 0) {
            const palette = generateColorSet(names);
            names.forEach((name) => {
                if (!map[name]) map[name] = palette[name];
            });
        } else {
            const palette = generateColorSet(allCustomers);
            allCustomers.forEach((name) => {
                if (!map[name]) map[name] = palette[name];
            });
        }
        return map;
    }, [customColorMap, selectedCustomers, allCustomers]);

    useEffect(() => {
        const initialCustomers = (allCompaniesMarginsPerDate?.results.slice(0, 3) || []).map((c) => c.customer_name);
        setSelectedCustomers((prev) => {
            if (prev.size > 0) {
                const filtered = new Set(Array.from(prev).filter((name) => allCustomers.includes(name)));
                if (filtered.size > 0) return filtered;
            }
            return new Set(initialCustomers);
        });
    }, [allCompaniesMarginsPerDate]);

    // ---------- Build chart series ----------
    const { series, colors, dashes } = useMemo(() => {
        const s = [];
        const c = [];
        const d = [];

        (allCompaniesMarginsPerDate?.results || []).forEach((cust, idx) => {
            if (!selectedCustomers.has(cust.customer_name)) return;

            const baseColor = uniqueColors[cust.customer_name] || uniqueColors[idx];

            // points for each metric
            const ptsRevenue = (cust.series || [])
                .map((p) => {
                    const x = toLocalTimestamp(p.date);
                    if (x === null) return null;
                    return { x, y: Number(p.revenue || 0) };
                })
                .filter(Boolean);
            const ptsProfit = (cust.series || [])
                .map((p) => {
                    const x = toLocalTimestamp(p.date);
                    if (x === null) return null;
                    return { x, y: Number(p.profit || 0) };
                })
                .filter(Boolean);
            const ptsLoads = (cust.series || [])
                .map((p) => {
                    const x = toLocalTimestamp(p.date);
                    if (x === null) return null;
                    return { x, y: Number(p.loads || 0) };
                })
                .filter(Boolean);

            // Revenue (solid)
            if (showRevenue) {
                s.push({
                    name: `${cust.customer_name} — Revenue`,
                    data: ptsRevenue,
                    type: 'line',
                    yaxisIndex: 1,
                    // Revenue (large value) now maps to Index 1 (Revenue/Profit axis)
                });
                c.push(baseColor);
                d.push(0); // solid
            }

            // Profit (dashed)
            if (showProfit) {
                s.push({
                    name: `${cust.customer_name} — Profit`,
                    data: ptsProfit,
                    type: 'line',
                    yaxisIndex: 1,
                    // Profit (large value) now maps to Index 1 (Revenue/Profit axis)
                });
                c.push(baseColor);
                d.push(7); // dashed
            }

            // Loads (dotted)
            if (showLoads) {
                s.push({
                    name: `${cust.customer_name} — Loads`,
                    data: ptsLoads,
                    type: 'line',
                    yaxisIndex: 0,
                    // Loads (small value) now maps to Index 0 (Loads axis)
                });
                c.push(baseColor);
                d.push(2); // dotted-ish
            }
        });

        // Return series, colors, and dashes
        return { series: s, colors: c, dashes: d };
    }, [allCompaniesMarginsPerDate, selectedCustomers, showRevenue, showProfit, showLoads]);

    // ---------- Chart options ----------
    const options = {
        chart: {
            type: 'line',
            toolbar: { show: false },
            animations: { enabled: true },
            zoom: { enabled: false },
            fontFamily:
                'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
        },
        stroke: {
            width: 2.5,
            curve: 'smooth',
            dashArray: dashes,
        },
        colors,
        markers: { size: 0, hover: { size: 4 } },
        legend: {
            show: false,
        },
        xaxis: {
            type: 'datetime',
            labels: {
                datetimeUTC: false,
                style: { fontWeight: 600 },
                // This only influences label formatting; actual aggregation must come from the API.
                format: granularity === 'month' ? "MMM 'yy" : undefined,
            },
            tooltip: { enabled: false },
        },
        yaxis: [
            {
                // Loads axis
                show: showLoads,
                opposite: true,
                decimalsInFloat: 0,
                forceNiceScale: true,
                labels: {
                    show: true,
                    formatter: (val) => `${Math.round(val ?? 0)}`,
                    style: { fontWeight: 700 },
                },
                axisBorder: { show: true },
                axisTicks: { show: true },
                title: { text: 'Loads', style: { fontWeight: 700 } },
                seriesName: allCustomers.map(c => `${c} — Loads`),
            },
            {
                // Revenue / Profit axis
                show: true,
                opposite: false,
                tickAmount: 8,
                labels: {
                    show: true,
                    formatter: (val) => shortCurrencyFormatter(val ?? 0),
                    style: { fontWeight: 700 },
                },
                axisBorder: { show: true },
                axisTicks: { show: true },
                title: { text: 'Revenue / Profit', style: { fontWeight: 700 } },
                seriesName: allCustomers.map(c => `${c} — Revenue`).concat(allCustomers.map(c => `${c} — Profit`)),
            },
        ],
        grid: {
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
            padding: { left: 6, right: 6, top: 8, bottom: 6 },
        },
        tooltip: {
            shared: true,
            x: {
                format: granularity === 'month' ? 'MMM yyyy' : 'MMM dd, yyyy'
            },
            y: {
                formatter: (val, { seriesIndex, w }) => {
                    const name = w?.globals?.seriesNames?.[seriesIndex] || '';
                    if (name.endsWith('Loads')) {
                        return `Loads: ${Math.round(val ?? 0)}`;
                    }
                    const label = name.includes('Profit') ? 'Profit' : 'Revenue';
                    return `${label}: ${currencyFormatter.format(val ?? 0)}`;
                },
            },
        },
    };

    // ---------- UI controls ----------
    const toggleCustomer = (name) => {
        setSelectedCustomers((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    const allSelected = selectedCustomers.size === allCustomers.length;
    const noneSelected = selectedCustomers.size === 0;

    const selectAll = () => setSelectedCustomers(new Set(allCustomers));
    const selectNone = () => setSelectedCustomers(new Set());

    const handleGranularityChange = (e) => {
        const g = e.target.value == 'month' ? 'month' : 'day';
        setGranularity(g);
    };

    return (
        <div className="customers-view">
            {/* Controls */}
            <div className="customers-filters">

                {/* Customer toggles */}
                <div className="customers-filters__group">
                    <span className="customers-filters__title">Select customers to graph</span>
                    {/* Companies multiselect */}
                    <SearchableMultiSelect
                        options={(allCompaniesMarginsPerDate?.results || []).map((c) => ({
                            label: c.customer_name,
                            value: c.customer_name,
                        }))}
                        value={Array.from(selectedCustomers)}
                        onChange={(selectedList) => setSelectedCustomers(new Set(selectedList))}
                        placeholder="Select customers..."
                        maxMenuHeight={200}
                    />
                </div>

                {/* Granularity */}
                <label htmlFor="granularity-select" className="customers-filters__group customers-filters__group--granularity">
                    <span className="customers-filters__title">Sample rate</span>
                    <div className="customers-filters__select-wrapper">
                        <select
                            id="granularity-select"
                            className='granularity-select'
                            value={granularity}
                            onChange={handleGranularityChange}
                            aria-label="Granularity"
                        >
                            <option value="day">Daily</option>
                            <option value="month">Monthly</option>
                        </select>
                    </div>
                </label>

            </div>

            {/* Chart */}
            <div className='chart-container' style={{ '--chart-height': `${height}px` }}>
                <div className="chart-container__chart">
                    <ReactApexChart options={options} series={series} type="line" height={height} />
                </div>
                <div className="legend-panel">
                    {
                        Array.from(selectedCustomers).map((cust, idx) => (
                            cust && (
                                <div
                                    key={cust}
                                    className="legend-panel__item"
                                    style={{ '--legend-color': uniqueColors[cust] }}
                                >
                                    <span className="legend-panel__color-swatch" />
                                    <span className="legend-panel__label">{cust}</span>
                                </div>
                            )
                        ))
                    }
                </div>

                {/* Metrics */}
                <div className="metrics-panel">
                    <span className="metrics-panel__title">Metrics</span>

                    <div className="metrics-panel__options">
                        {/* Revenue (solid) */}
                        <label
                            className={`metric-toggle metric-toggle--revenue${showRevenue ? ' is-active' : ''}`}
                        >
                            <input
                                className="metric-toggle__checkbox"
                                type="checkbox"
                                checked={showRevenue}
                                onChange={(e) => setShowRevenue(e.target.checked)}
                            />
                            <span className="metric-toggle__content">
                                {/* color swatch */}
                                <span className="metric-toggle__label">Revenue</span>
                                {/* line sample (solid) */}
                                <span aria-hidden title="Solid line" className="metric-toggle__line metric-toggle__line--solid" />
                            </span>
                        </label>

                        {/* Profit (dashed) */}
                        <label
                            className={`metric-toggle metric-toggle--profit${showProfit ? ' is-active' : ''}`}
                        >
                            <input
                                className="metric-toggle__checkbox"
                                type="checkbox"
                                checked={showProfit}
                                onChange={(e) => setShowProfit(e.target.checked)}
                            />
                            <span className="metric-toggle__content">
                                <span className="metric-toggle__label">Profit</span>
                                {/* line sample (dashed) */}
                                <span aria-hidden title="Dashed line" className="metric-toggle__line metric-toggle__line--dashed" />
                            </span>
                        </label>

                        {/* Loads (dotted) */}
                        <label
                            className={`metric-toggle metric-toggle--loads${showLoads ? ' is-active' : ''}`}
                        >
                            <input
                                className="metric-toggle__checkbox"
                                type="checkbox"
                                checked={showLoads}
                                onChange={(e) => setShowLoads(e.target.checked)}
                            />
                            <span className="metric-toggle__content">
                                <span className="metric-toggle__label">Loads</span>
                                {/* line sample (dotted) */}
                                <span aria-hidden title="Dotted line" className="metric-toggle__line metric-toggle__line--dotted" />
                            </span>
                        </label>
                    </div>

                </div>

            </div>

        </div>
    );
}

export default AllCustomersRevenueProfitChart;
